import { element, globalEvent } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';
import { addCss, hide, visible, parseToBoolean } from '@shared/easyfy.utils';
import basket from '@webModules/basket';
import { Backdrop } from '@webModules/backdrop';
import prescriptionService from '@webServices/prescription.service';

namespace Meds.Modules {
  @invoke
  export class MedsBasket {

    onInit() {
      globalEvent({
        type: 'click',
        event: {
          action: 'prescriptioncartrow-delete',
          callback: MedsBasket.onDeletePrescriptionRow
        }
      });
    }

    /** Removes a prescription row from the cart, shows a loading state on the button, and re-renders the basket on success. */
    static onDeletePrescriptionRow = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        const elem: HTMLElement = e.target as HTMLElement;

        if (elem.getAttribute('data-disabled')) {
          return;
        }

        elem.setAttribute('data-disabled', 'true');
        const originalClasses = Array.from(elem.classList);
        elem.removeAttribute('class');
        addCss(elem, ['loader', 'loader-mini']);

        const token = elem.attributes['data-token']?.value;
        const cartId = elem.attributes['data-cartId']?.value;
        const prescriptionId = elem.attributes['data-prescriptionId']?.value;
        const receptRadId = elem.attributes['data-receptRadId']?.value;
        const prescriptionWithBenefit =
          elem.attributes['data-prescriptionWithBenefit']?.value;
        const price = elem.attributes['data-price']?.value;
        const varunr = elem.attributes['data-varunr']?.value;
        const nplPackId = elem.attributes['data-nplPackId']?.value;

        const data: prescriptionService.DeleteCartRowCommand = {
          cartId,
          prescriptionId: parseInt(prescriptionId, 10),
          prescriptionWithBenefit: parseToBoolean(prescriptionWithBenefit),
          price: Number.parseFloat(price),
          receptRadId,
          varunr,
          nplPackId,
          renderMiniCart: true
        };

        prescriptionService
          .DeleteCartRow(data, token)
          .then((response) => {
            if (
              response.status !== 200 ||
              response.data == null ||
              !response.data.success
            ) {
              return;
            }

            basket.Basket.renderBasket(response);

            MedsBasket.openRx(cartId);

            // Send event which causes reload for prescription list/details
            MedsBasket.triggerReloadForRxPages();
          })
          .catch((error) => {
            console.log(error);

            elem.removeAttribute('data-disabled');
            elem.removeAttribute('class');
            addCss(elem, originalClasses);
          });
      } catch (error) {
        console.error(error);
      }
    };

    /** Dispatches a `deletePrescriptionRow` event to trigger a reload on prescription list/detail pages. */
    static triggerReloadForRxPages = () => {
      const deletePrescriptionRowEvent = new Event('deletePrescriptionRow', { bubbles: true, composed: true });

      document.dispatchEvent(deletePrescriptionRowEvent);
    };

    /** Re-opens the prescription cart details section for the given cart ID. */
    static openRx = async (cartId: string) => {

      try {
        const prevOpenBasket = element(
          `.prescription-cart-details summary[data-cartid="${cartId}"]`
        ) as HTMLElement;

        if (!prevOpenBasket) {
          return;
        }

        prevOpenBasket.click();
      } catch (error) {
        console.error(error);
      }
    };

    /** Closes the mini-basket and hides the backdrop. */
    static onCloseBasket = async (e) => {

      try {
        const toggleBasketElement: HTMLElement = element('[data-action="toggle-mini-basket"]');
        const menu: HTMLElement = element(`[data-container="${toggleBasketElement.attributes['data-target']?.value}"]`);

        if (menu instanceof HTMLDialogElement) {
          // The menu is a dialog element, so we can use a simplified way to close it.
          menu.close();
        } else {
          // The menu is not a dialog element, so we need a more complex way to close it.
          const basketOverlay: HTMLElement = document.querySelector('[data-container="mini-basket"]');
          if (visible(basketOverlay)) {
            Backdrop.hide();
          } else {
            Backdrop.show('backdrop-page');
          }
          if (toggleBasketElement.classList.contains('menu-isShown')) {
            toggleBasketElement.classList.remove('menu-isShown');
            toggleBasketElement.setAttribute('aria-expanded', 'false');
            hide(menu);
          }
        }
      } catch (error) {
        console.error(error);
      }
    };


    /** Returns the CSS selector for a human prescription details element by ordination ID. */
    static getHumanPrescriptionSelector = (prescriptionId: number) =>
      `[data-action="prescription-details"][data-prescription-ordinationid="${prescriptionId}"]`;

    /** Returns the CSS selector for an animal prescription details element by prescription ID. */
    static getAnimalPrescriptionSelector = (prescriptionId: number) =>
      `[data-action="animal-prescription-details"][data-prescription-id="${prescriptionId}"]`;
  }
}

export default Meds.Modules;
